/**
 * Строка таблицы клиента (Stripe/Linear-style).
 */

import React from "react";
import { ChevronRight } from "lucide-react";
import type { ClientInfo } from "@/services/api";
import { RISK_STYLES } from "@/constants/riskLevels";
import { cn } from "@/utils";

interface ClientRowProps {
  client: ClientInfo;
  onViewDetails?: (clientId: number) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: "Активен", color: RISK_STYLES.A.color },
  NOT_ACTIVATED: { label: "Не активирован", color: RISK_STYLES.B.color },
  SUSPENDED: { label: "Приостановлен", color: RISK_STYLES.C.color },
  BLOCKED: { label: "Заблокирован", color: RISK_STYLES.D.color },
};

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

const formatRub = (value: number | null | undefined): string => {
  if (value == null) return "—";
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value) + " ₽";
};

const formatDays = (days: number | null | undefined): string => {
  if (days == null) return "—";
  if (days <= 0) return "истекла";
  if (days === 1) return "1 день";
  if (days < 5) return `${days} дня`;
  return `${days} дней`;
};

const ClientRow: React.FC<ClientRowProps> = ({ client, onViewDetails }) => {
  const { organization, users_count, active_users_count, balance, days_remaining } = client;
  const statusConfig = STATUS_CONFIG[organization.status] ?? STATUS_CONFIG.BLOCKED;
  const displayName = organization.short_name || organization.full_name;

  return (
    <tr
      className="group border-b border-line transition-colors hover:bg-surface-hover cursor-pointer"
      onClick={() => onViewDetails?.(organization.id)}
    >
      {/* ИНН */}
      <td className="px-3 py-2.5 text-fs-xs font-mono tabular-nums text-ink-2 whitespace-nowrap">{organization.inn}</td>

      {/* Наименование */}
      <td className="px-3 py-2.5">
        <div className="text-fs-sm text-ink-1 truncate max-w-md" title={displayName}>
          {displayName}
        </div>
        {organization.short_name && organization.short_name !== organization.full_name && (
          <div className="mt-0.5 text-fs-2xs text-ink-3 truncate max-w-md" title={organization.full_name}>
            {organization.full_name}
          </div>
        )}
      </td>

      {/* Статус */}
      <td className="px-3 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-fs-xs text-ink-2 whitespace-nowrap">
          <span
            className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ backgroundColor: statusConfig.color }}
            aria-hidden
          />
          {statusConfig.label}
        </span>
      </td>

      {/* Пользователи */}
      <td className="px-3 py-2.5 text-fs-xs text-right whitespace-nowrap font-mono tabular-nums">
        <span className="text-ink-1">{active_users_count}</span>
        <span className="text-ink-4"> / {users_count}</span>
      </td>

      {/* Баланс */}
      <td className="px-3 py-2.5 text-fs-xs text-right whitespace-nowrap font-mono tabular-nums">
        <span className={cn(balance != null ? "text-ink-1" : "text-ink-4")}>{formatRub(balance)}</span>
      </td>

      {/* Подписка (дней) */}
      <td className="px-3 py-2.5 text-fs-xs text-right whitespace-nowrap font-mono tabular-nums">
        <span
          className={cn(
            days_remaining == null
              ? "text-ink-4"
              : days_remaining <= 0
                ? "text-error-600"
                : days_remaining < 7
                  ? "text-warning-700"
                  : "text-ink-1",
          )}
        >
          {formatDays(days_remaining)}
        </span>
      </td>

      {/* Дата регистрации */}
      <td className="px-3 py-2.5 text-fs-xs text-ink-3 whitespace-nowrap">{formatDate(organization.created_at)}</td>

      {/* Действия — стрелка только */}
      <td className="px-3 py-2.5 text-right">
        <ChevronRight
          size={14}
          strokeWidth={1.75}
          className={cn("text-ink-4 group-hover:text-ink-1 transition-colors inline-block")}
        />
      </td>
    </tr>
  );
};

export default ClientRow;
